import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { platformAPI } from '../services/api';

const WhotResults = () => {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [liveChargePercent, setLiveChargePercent] = useState(0);

  useEffect(() => {
    if (!challengeId) return;

    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await api.get(`/multiplayer/${challengeId}`);
        const data = res.data?.challenge;
        if (!data) {
          setError('Results not found');
          return;
        }
        if (String(data.game || '').toLowerCase() !== 'whot') {
          setError('Not a WHOT game');
          return;
        }
        setChallenge(data);
      } catch (err) {
        setError(err?.response?.data?.error || err.message || 'Failed to load results');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [challengeId]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await platformAPI.getSettings();
        const charge = Number(res?.data?.chargePercent);
        if (Number.isFinite(charge) && charge >= 0) {
          setLiveChargePercent(charge);
        }
      } catch {
        // ignore settings failures for results display
      }
    };

    loadSettings();
  }, []);

  const participantMap = useMemo(() => {
    const map = new Map();
    if (challenge?.participants) {
      (challenge.participants || []).forEach((p) => {
        if (p && typeof p.id !== 'undefined') {
          map.set(Number(p.id), p.username || `Player ${p.id}`);
        }
      });
    }
    if (challenge?.creator_id && challenge?.creator_username) {
      map.set(Number(challenge.creator_id), challenge.creator_username);
    }
    return map;
  }, [challenge]);

  const placement = useMemo(() => {
    const raw = challenge?.placement;
    const list = Array.isArray(raw) ? raw : [];
    return list
      .map((entry) => ({
        userId: Number(entry.userId),
        rank: entry.rank,
        score: entry.score,
        name: participantMap.get(Number(entry.userId)) || `Player ${entry.userId}`
      }))
      .sort((a, b) => a.rank - b.rank);
  }, [challenge, participantMap]);

  const prizeMap = useMemo(() => {
    const raw = challenge?.prize_distribution || {};
    if (raw && typeof raw === 'object') return raw;
    return {};
  }, [challenge]);

  const potSummary = useMemo(() => {
    const wager = Number(challenge?.wager || 0);
    const players = Number(challenge?.current_players || 0) || placement.length;
    const grossPot = wager * players;
    const challengeChargePercent = Number(challenge?.charge_percent);
    const chargePercent = Number.isFinite(challengeChargePercent) && challengeChargePercent >= 0
      ? challengeChargePercent
      : Number(liveChargePercent || 0);
    const chargeAmount = Number(challenge?.charge_amount || 0) || Math.floor((grossPot * chargePercent) / 100);
    const netPot = Number(challenge?.net_pot || 0) || Math.max(0, grossPot - chargeAmount);
    return { grossPot, chargePercent, chargeAmount, netPot };
  }, [challenge, placement.length, liveChargePercent]);

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <h1>🃏 WHOT Results</h1>
        <div className="header-actions">
          <button className="multiplayer-btn" onClick={() => navigate('/history')}>
            Back to History
          </button>
        </div>
      </header>

      <main className="dashboard-content">
        {loading && (
          <section className="profile-card">
            <p>Loading results...</p>
          </section>
        )}

        {!loading && error && (
          <section className="profile-card">
            <p style={{ color: 'crimson' }}>{error}</p>
          </section>
        )}

        {!loading && !error && challenge && (
          <section className="profile-card">
            <h2>Game #{challenge.id}</h2>
            <p>Players: {challenge.current_players}/{challenge.max_players}</p>
            <p>Mode: {Number(challenge.wager || 0) > 0 ? 'Betting' : 'Friendly'}</p>
            {Number(challenge.wager || 0) > 0 && (
              <p>
                Pot {potSummary.grossPot} • Charge {potSummary.chargeAmount} ({potSummary.chargePercent}%) • Net {potSummary.netPot}
              </p>
            )}

            {placement.length === 0 ? (
              <p>No placement results recorded.</p>
            ) : (
              <div className="whot-scoreboard whot-scoreboard--results" style={{ marginTop: 12 }}>
                <strong>Results</strong>
                <div className="whot-score-list">
                  {placement.map((entry) => (
                    <div key={entry.userId} className={`whot-score-item whot-score-item--rank-${entry.rank}`}>
                      <span>#{entry.rank} {entry.name}</span>
                      <span className="whot-muted">Card Left: {entry.score}</span>
                      <span className="whot-muted">Prize: {Number(prizeMap?.[entry.userId] || 0)} wikipenny</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button 
                className="whot-btn" 
                onClick={() => {
                  const tId = challenge?.tournament_id || challenge?.meta?.tournamentId;
                  if (tId) {
                    navigate(`/whot-tournament/${tId}`);
                  } else {
                    navigate('/whot-online');
                  }
                }}
              >
                { (challenge?.tournament_id || challenge?.meta?.tournamentId) ? 'Back to Tournament' : 'Back to Lobby' }
              </button>
              <button className="whot-btn" onClick={() => navigate('/history')}>
                View All History
              </button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default WhotResults;

